/**
 * sl-3nkm.8.4: Pure math for the `ExecutionPanel` timeout warning.
 * Kept in its own file so the tests can import it without pulling in
 * the React component tree (the panel transitively imports `lit` via
 * `@affine/component` tooltips, which is expensive to bootstrap in
 * unit tests).
 */

export type TimeoutState = 'normal' | 'warning' | 'error';

/**
 * Convert the elapsed-since-start signal into a normal/warning/error
 * state gated on the plan's `maxStepDurationMs`.
 *
 * Boundaries:
 *   - `normal`  — elapsedMs < 0.8 * max
 *   - `warning` — 0.8 * max <= elapsedMs < max
 *   - `error`   — elapsedMs >= max (step has run past the server-side
 *                 ceiling; the runner will surface the timeout shortly)
 *
 * `remainingMs` saturates at 0 in the error branch so tooltips can
 * render a non-negative countdown.
 */
export function computeTimeoutState(
  elapsedMs: number,
  maxStepDurationMs: number
): { state: TimeoutState; remainingMs: number } {
  const ratio = elapsedMs / maxStepDurationMs;
  const remainingMs = Math.max(0, maxStepDurationMs - elapsedMs);
  if (ratio >= 1) return { state: 'error', remainingMs: 0 };
  if (ratio >= 0.8) return { state: 'warning', remainingMs };
  return { state: 'normal', remainingMs };
}
